import { useEffect, useRef, useState, type ReactNode } from "react";
import { useIntl } from "react-intl";
import { Plus, X } from "lucide-react";
import type { SocialPostPlatform, SocialPostType } from "@runcheetah/shared";

import {
  addHashtagToken,
  formatHashtagToken,
  removeHashtagToken,
  updateHashtagToken,
  type SocialPostChromeVariant,
} from "@/lib/social-post-chrome";
import { SocialPostPreview, type SocialPostPreviewLabels } from "@runcheetah/ui";

export type InstagramPostChromeProps = {
  platform: SocialPostPlatform;
  postType: SocialPostType;
  profileLabel: string;
  media: ReactNode;
  mediaOverlay?: ReactNode;
  caption: string;
  hashtags: string[];
  className?: string;
  headerAction?: ReactNode;
  showMetadata?: boolean;
};

export type InstagramPostMetadataEditorProps = {
  variant: SocialPostChromeVariant;
  profileLabel: string;
  mediaWidth: number;
  mediaHeight: number;
  caption: string;
  hashtags: string[];
  onCaptionChange?: (caption: string) => void;
  onHashtagsChange?: (hashtags: string[]) => void;
  onCaptionDoubleClick?: () => void;
};

export function InstagramPostChrome({
  platform,
  postType,
  profileLabel,
  media,
  mediaOverlay,
  caption,
  hashtags,
  className,
  headerAction,
  showMetadata = true,
}: InstagramPostChromeProps) {
  const intl = useIntl();

  return (
    <SocialPostPreview
      platform={platform}
      postType={postType}
      name={profileLabel}
      media={media}
      mediaOverlay={mediaOverlay}
      caption={caption}
      hashtags={hashtags}
      className={className}
      headerAction={headerAction}
      showMetadata={showMetadata}
      labels={getInstagramPostChromeLabels(intl)}
    />
  );
}

function getInstagramPostChromeLabels(intl: ReturnType<typeof useIntl>): SocialPostPreviewLabels {
  return {
    postMenu: intl.formatMessage({
      id: "instagramPostChrome.postMenuPreview",
      defaultMessage: "Instagram post menu preview",
    }),
    like: intl.formatMessage({
      id: "instagramPostChrome.likePreview",
      defaultMessage: "Like preview",
    }),
    comment: intl.formatMessage({
      id: "instagramPostChrome.commentPreview",
      defaultMessage: "Comment preview",
    }),
    share: intl.formatMessage({
      id: "instagramPostChrome.sharePreview",
      defaultMessage: "Share preview",
    }),
    save: intl.formatMessage({
      id: "instagramPostChrome.savePreview",
      defaultMessage: "Save preview",
    }),
    storyMore: intl.formatMessage({
      id: "instagramPostChrome.storyMorePreview",
      defaultMessage: "Story more preview",
    }),
    closeStory: intl.formatMessage({
      id: "instagramPostChrome.closeStoryPreview",
      defaultMessage: "Close story preview",
    }),
    sendMessage: intl.formatMessage({
      id: "instagramPostChrome.sendMessage",
      defaultMessage: "Send message",
    }),
    likeStory: intl.formatMessage({
      id: "instagramPostChrome.likeStoryPreview",
      defaultMessage: "Like story preview",
    }),
    shareStory: intl.formatMessage({
      id: "instagramPostChrome.shareStoryPreview",
      defaultMessage: "Share story preview",
    }),
    storyMetadata: intl.formatMessage({
      id: "instagramPostChrome.storyMetadata",
      defaultMessage: "Story metadata",
    }),
    captionPending: intl.formatMessage({
      id: "instagramPostChrome.captionPending",
      defaultMessage: "Caption pending",
    }),
  };
}

export function InstagramPostMetadataEditor({
  variant,
  profileLabel,
  mediaWidth,
  mediaHeight,
  caption,
  hashtags,
  onCaptionChange,
  onHashtagsChange,
  onCaptionDoubleClick,
}: InstagramPostMetadataEditorProps) {
  const intl = useIntl();

  if (variant === "instagram_story") {
    return (
      <div
        className="pointer-events-auto absolute rounded-[2rem] border border-black/[0.08] bg-white/98 p-6 text-[#0f0f10] shadow-[0_18px_48px_rgba(15,15,16,0.14)]"
        style={{ left: mediaWidth + 80, top: 40, width: 280 }}
      >
        <div className="mb-5 text-[13px] font-semibold uppercase tracking-[0.18em] text-[#737373]">
          {intl.formatMessage({
            id: "instagramPostChrome.storyMetadata",
            defaultMessage: "Story metadata",
          })}
        </div>
        <div className="space-y-4 text-[18px] leading-[1.35]">
          <EditableCaption
            profileLabel={profileLabel}
            value={caption}
            onChange={onCaptionChange}
            onDoubleClick={onCaptionDoubleClick}
            placeholder={intl.formatMessage({
              id: "instagramPostChrome.addStoryCaptionPlaceholder",
              defaultMessage: "Double-click to add story caption",
            })}
          />
          <EditableHashtagTokens tokens={hashtags} onChange={onHashtagsChange} />
        </div>
      </div>
    );
  }

  return (
    <div
      className="pointer-events-auto absolute space-y-3 px-12 pb-9 text-[22px] leading-[1.34] text-[#0f0f10]"
      style={{ left: 0, top: 84 + mediaHeight + 64, width: mediaWidth + 96 }}
    >
      <EditableCaption
        profileLabel={profileLabel}
        value={caption}
        onChange={onCaptionChange}
        onDoubleClick={onCaptionDoubleClick}
        placeholder={intl.formatMessage({
          id: "instagramPostChrome.addCaptionPlaceholder",
          defaultMessage: "Double-click to add a caption",
        })}
      />
      <EditableHashtagTokens tokens={hashtags} onChange={onHashtagsChange} />
    </div>
  );
}

function EditableCaption({
  profileLabel,
  value,
  placeholder,
  onChange,
  onDoubleClick: onDoubleClickOverride,
}: {
  profileLabel: string;
  value: string;
  placeholder: string;
  onChange?: (value: string) => void;
  onDoubleClick?: () => void;
}) {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(value);
    }
  }, [isEditing, value]);

  useEffect(() => {
    if (isEditing) {
      textareaRef.current?.focus();
      textareaRef.current?.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <textarea
        ref={textareaRef}
        value={draft}
        rows={3}
        className="min-h-[5.6em] w-full resize-none rounded-2xl border border-black/15 bg-white/96 px-4 py-3 text-[1em] leading-[1.32] outline-none ring-0 focus:border-black/30"
        aria-label={intl.formatMessage({
          id: "instagramPostChrome.editCaptionAria",
          defaultMessage: "Edit Instagram caption",
        })}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          setIsEditing(false);
          onChange?.(draft.trim());
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            setIsEditing(false);
            setDraft(value);
          }

          if (event.key === "Enter" && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            setIsEditing(false);
            onChange?.(draft.trim());
          }
        }}
      />
    );
  }

  return (
    <div
      className="cursor-text rounded-xl outline-none focus-visible:ring-2 focus-visible:ring-black/18"
      role="button"
      tabIndex={0}
      title={intl.formatMessage({
        id: "instagramPostChrome.editCaptionTitle",
        defaultMessage: "Double-click to edit caption",
      })}
      onDoubleClick={() => {
        if (onDoubleClickOverride) {
          onDoubleClickOverride();
          return;
        }

        setIsEditing(true);
      }}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          if (onDoubleClickOverride) {
            onDoubleClickOverride();
            return;
          }

          setIsEditing(true);
        }
      }}
    >
      <span className="font-semibold">{profileLabel}</span>{" "}
      <span className="whitespace-pre-wrap text-[#1f1f22]">{value.trim() || placeholder}</span>
    </div>
  );
}

function EditableHashtagTokens({
  tokens,
  onChange,
}: {
  tokens: string[];
  onChange?: (tokens: string[]) => void;
}) {
  const intl = useIntl();
  const [adding, setAdding] = useState(false);
  const [draftToken, setDraftToken] = useState("");
  const addInputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (adding) {
      addInputRef.current?.focus();
    }
  }, [adding]);

  const commitAdd = () => {
    const next = addHashtagToken(tokens, draftToken);
    setAdding(false);
    setDraftToken("");
    if (next !== tokens) {
      onChange?.(next);
    }
  };

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-2 text-[#0b54d6]">
      {tokens.map((token, index) => (
        <EditableHashtagToken
          key={`${token}-${index}`}
          token={token}
          onUpdate={(value) => onChange?.(updateHashtagToken(tokens, index, value))}
          onRemove={() => onChange?.(removeHashtagToken(tokens, index))}
        />
      ))}
      {adding ? (
        <input
          ref={addInputRef}
          value={draftToken}
          aria-label={intl.formatMessage({
            id: "instagramPostChrome.addHashtag",
            defaultMessage: "Add hashtag",
          })}
          className="h-[1.9em] w-36 rounded-full border border-[#0b54d6]/25 bg-white/96 px-3 text-[0.82em] leading-none text-[#0b54d6] outline-none focus:border-[#0b54d6]/45"
          placeholder={intl.formatMessage({
            id: "instagramPostChrome.hashtagPlaceholder",
            defaultMessage: "#hashtag",
          })}
          onChange={(event) => setDraftToken(event.target.value)}
          onBlur={commitAdd}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              event.preventDefault();
              setAdding(false);
              setDraftToken("");
            }

            if (event.key === "Enter") {
              event.preventDefault();
              commitAdd();
            }
          }}
        />
      ) : (
        <button
          type="button"
          className="inline-flex h-[1.9em] items-center gap-1 rounded-full border border-[#0b54d6]/25 bg-white/85 px-2.5 text-[0.82em] font-medium leading-none text-[#0b54d6] shadow-[0_1px_0_rgba(11,84,214,0.06)]"
          aria-label={intl.formatMessage({
            id: "instagramPostChrome.addHashtag",
            defaultMessage: "Add hashtag",
          })}
          onClick={() => setAdding(true)}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {intl.formatMessage({ id: "instagramPostChrome.hashtag", defaultMessage: "Hashtag" })}
        </button>
      )}
    </div>
  );
}

function EditableHashtagToken({
  token,
  onUpdate,
  onRemove,
}: {
  token: string;
  onUpdate: (value: string) => void;
  onRemove: () => void;
}) {
  const intl = useIntl();
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(token);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!isEditing) {
      setDraft(token);
    }
  }, [isEditing, token]);

  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [isEditing]);

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        aria-label={intl.formatMessage(
          { id: "instagramPostChrome.editHashtag", defaultMessage: "Edit {hashtag}" },
          { hashtag: formatHashtagToken(token) },
        )}
        className="h-[1.9em] w-36 rounded-full border border-[#0b54d6]/25 bg-white/96 px-3 text-[0.82em] leading-none text-[#0b54d6] outline-none focus:border-[#0b54d6]/45"
        onChange={(event) => setDraft(event.target.value)}
        onBlur={() => {
          setIsEditing(false);
          onUpdate(draft);
        }}
        onKeyDown={(event) => {
          if (event.key === "Escape") {
            event.preventDefault();
            setIsEditing(false);
            setDraft(token);
          }

          if (event.key === "Enter") {
            event.preventDefault();
            setIsEditing(false);
            onUpdate(draft);
          }
        }}
      />
    );
  }

  return (
    <span className="group/hashtag inline-flex max-w-full items-center gap-1 rounded-full bg-[#0b54d6]/8 py-1 pl-2.5 pr-1 text-[0.82em] font-medium leading-none text-[#0b54d6] shadow-[0_1px_0_rgba(11,84,214,0.04)]">
      <button
        type="button"
        className="min-w-0 truncate rounded-full outline-none focus-visible:ring-2 focus-visible:ring-[#0b54d6]/25"
        title={intl.formatMessage({
          id: "instagramPostChrome.editHashtagTitle",
          defaultMessage: "Double-click to edit hashtag",
        })}
        onDoubleClick={() => setIsEditing(true)}
      >
        {formatHashtagToken(token)}
      </button>
      <button
        type="button"
        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[#0b54d6]/55 opacity-60 transition hover:bg-[#0b54d6]/10 group-hover/hashtag:opacity-100 focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0b54d6]/25"
        aria-label={intl.formatMessage(
          { id: "instagramPostChrome.removeHashtag", defaultMessage: "Remove {hashtag}" },
          { hashtag: formatHashtagToken(token) },
        )}
        onClick={onRemove}
      >
        <X className="h-3.5 w-3.5" aria-hidden="true" />
      </button>
    </span>
  );
}
